import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { format, subDays } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Embudo de ventas.
 *
 * Responde dos preguntas que hoy solo se pueden contestar leyendo
 * conversacion por conversacion:
 *
 *   1. ¿En que punto se quedo cada persona?
 *   2. De las que pusieron un pero, ¿cuantas terminaron comprando?
 *
 * La segunda es la que importa. Saber que la objecion de precio aparecio
 * treinta veces no dice nada por si solo; lo que dice algo es que de esas
 * treinta cerraron dos. Ahi esta el argumento que hay que cambiar.
 */

const useStyles = makeStyles(theme => ({
  raiz: {
    padding: theme.spacing(3),
    maxWidth: 1100,
    margin: "0 auto",
    width: "100%",
  },
  encabezado: {
    marginBottom: theme.spacing(3),
  },
  titulo: {
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },
  bajada: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    maxWidth: 620,
  },
  filtros: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  tarjetas: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  tarjeta: {
    position: "relative",
    padding: theme.spacing(2.5),
    borderRadius: 14,
    overflow: "hidden",
  },
  // Una franja de color arriba: sin ella las tarjetas se leen como cajas
  // grises y el panel se siente plano.
  franja: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
  },
  numero: {
    fontWeight: 700,
    fontSize: "2.4rem",
    lineHeight: 1.1,
    letterSpacing: "-0.03em",
  },
  rotulo: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
  },
  bloque: {
    padding: theme.spacing(3),
    borderRadius: 14,
    marginBottom: theme.spacing(3),
  },
  tituloBloque: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  notaBloque: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginBottom: theme.spacing(2.5),
  },
  etapa: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
  },
  etapaNombre: {
    width: 190,
    flexShrink: 0,
    fontSize: "0.9rem",
    fontWeight: 500,
    paddingRight: theme.spacing(1.5),
  },
  carril: {
    flexGrow: 1,
    height: 26,
    borderRadius: 6,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.045)",
    overflow: "hidden",
  },
  barra: {
    height: "100%",
    borderRadius: 6,
    transition: "width .45s ease",
    minWidth: 3,
  },
  etapaCuenta: {
    width: 86,
    flexShrink: 0,
    textAlign: "right",
    fontSize: "0.9rem",
    fontWeight: 600,
  },
  tabla: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: theme.spacing(1, 1.5),
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: theme.palette.text.secondary,
    fontWeight: 700,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  thNum: {
    textAlign: "right",
  },
  td: {
    padding: theme.spacing(1.5),
    fontSize: "0.92rem",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tdNum: {
    textAlign: "right",
    fontWeight: 600,
  },
  medidor: {
    display: "inline-block",
    verticalAlign: "middle",
    width: 64,
    height: 6,
    borderRadius: 3,
    marginRight: theme.spacing(1),
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  medidorRelleno: {
    height: "100%",
    borderRadius: 3,
  },
  aviso: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    borderLeft: "4px solid #e9582c",
    background:
      theme.palette.type === "dark"
        ? "rgba(233,88,44,0.12)"
        : "rgba(233,88,44,0.07)",
    marginTop: theme.spacing(3),
  },
  vacio: {
    textAlign: "center",
    padding: theme.spacing(5, 2),
    color: theme.palette.text.secondary,
  },
  cargando: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
}));

/** Verde si cierra bien, ambar si va a medias, rojo si no cierra. */
const colorPorCierre = porcentaje => {
  if (porcentaje >= 50) return "#219e7e";
  if (porcentaje >= 25) return "#d97706";
  return "#dc2626";
};

const Embudo = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  // Treinta dias hacia atras: una sola campaña rara vez cabe en un dia, y
  // abrir la pantalla en ceros haria parecer que no funciona.
  const [desde, setDesde] = useState(
    format(subDays(new Date(), 30), "yyyy-MM-dd")
  );
  const [hasta, setHasta] = useState(format(new Date(), "yyyy-MM-dd"));

  const consultar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/reporte/embudo", {
        params: { desde, hasta },
      });
      setDatos(data);
    } catch (err) {
      toastError(err);
    } finally {
      setCargando(false);
    }
  }, [desde, hasta]);

  useEffect(() => {
    consultar();
    // Solo al abrir: después se consulta con el botón, para que cambiar una
    // fecha no dispare una consulta por cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mayorEtapa = datos
    ? Math.max(1, ...datos.etapas.map(e => e.cuantas))
    : 1;

  return (
    <div className={classes.raiz}>
      <div className={classes.encabezado}>
        <Typography variant="h5" className={classes.titulo}>
          Embudo de ventas
        </Typography>
        <Typography className={classes.bajada}>
          En qué punto se quedó cada conversación y qué objeciones aparecieron.
          La última columna es la que importa: de las que pusieron ese pero,
          cuántas terminaron comprando.
        </Typography>
      </div>

      <div className={classes.filtros}>
        <TextField
          label="Desde"
          type="date"
          value={desde}
          onChange={e => setDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
        />
        <TextField
          label="Hasta"
          type="date"
          value={hasta}
          onChange={e => setHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
        />
        <Button
          variant="contained"
          color="primary"
          onClick={consultar}
          disabled={cargando}
        >
          Consultar
        </Button>
      </div>

      {cargando && (
        <div className={classes.cargando}>
          <CircularProgress />
        </div>
      )}

      {!cargando && datos && datos.total === 0 && (
        <Paper elevation={0} className={classes.bloque}>
          <div className={classes.vacio}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Todavía no hay conversaciones en estas fechas
            </Typography>
            <Typography style={{ marginTop: 8 }}>
              Amplía el rango de fechas o espera a que entren los primeros
              mensajes de la campaña.
            </Typography>
          </div>
        </Paper>
      )}

      {!cargando && datos && datos.total > 0 && (
        <>
          <div className={classes.tarjetas}>
            <Paper elevation={0} className={classes.tarjeta}>
              <div
                className={classes.franja}
                style={{
                  background:
                    "linear-gradient(90deg, #8b6fd4 0%, #a855f7 100%)",
                }}
              />
              <Typography className={classes.numero}>{datos.total}</Typography>
              <Typography className={classes.rotulo}>Conversaciones</Typography>
            </Paper>

            <Paper elevation={0} className={classes.tarjeta}>
              <div
                className={classes.franja}
                style={{
                  background:
                    "linear-gradient(90deg, #219e7e 0%, #10b981 100%)",
                }}
              />
              <Typography className={classes.numero}>
                {datos.cerradas}
              </Typography>
              <Typography className={classes.rotulo}>Compraron</Typography>
            </Paper>

            <Paper elevation={0} className={classes.tarjeta}>
              <div
                className={classes.franja}
                style={{
                  background:
                    "linear-gradient(90deg, #e9582c 0%, #f97316 100%)",
                }}
              />
              <Typography className={classes.numero}>
                {datos.porcentajeCierre}%
              </Typography>
              <Typography className={classes.rotulo}>Cierre</Typography>
            </Paper>
          </div>

          <Paper elevation={0} className={classes.bloque}>
            <Typography variant="h6" className={classes.tituloBloque}>
              Dónde se quedó cada quien
            </Typography>
            <Typography className={classes.notaBloque}>
              Cada barra es una etapa del tablero. «Sin etapa» son las
              conversaciones que todavía no se clasifican.
            </Typography>

            {datos.etapas.map(etapa => (
              <div className={classes.etapa} key={etapa.nombre}>
                <div className={classes.etapaNombre}>{etapa.nombre}</div>
                <div className={classes.carril}>
                  <div
                    className={classes.barra}
                    style={{
                      width: `${(etapa.cuantas / mayorEtapa) * 100}%`,
                      backgroundColor: etapa.color,
                    }}
                  />
                </div>
                <div className={classes.etapaCuenta}>
                  {etapa.cuantas}
                  <span
                    style={{
                      color: theme.palette.text.secondary,
                      fontWeight: 400,
                      marginLeft: 6,
                    }}
                  >
                    {Math.round((etapa.cuantas / datos.total) * 100)}%
                  </span>
                </div>
              </div>
            ))}
          </Paper>

          <Paper elevation={0} className={classes.bloque}>
            <Typography variant="h6" className={classes.tituloBloque}>
              Objeciones
            </Typography>
            <Typography className={classes.notaBloque}>
              Ordenadas por las que menos cierran: lo de arriba es lo que hay
              que reforzar primero.
            </Typography>

            {datos.objeciones.length === 0 ? (
              <Typography className={classes.vacio}>
                Todavía no se ha anotado ninguna objeción.
              </Typography>
            ) : (
              <>
                <table className={classes.tabla}>
                  <thead>
                    <tr>
                      <th className={classes.th}>Objeción</th>
                      <th className={`${classes.th} ${classes.thNum}`}>
                        Apareció
                      </th>
                      <th className={`${classes.th} ${classes.thNum}`}>
                        Compraron
                      </th>
                      <th className={`${classes.th} ${classes.thNum}`}>
                        Cierre
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.objeciones.map(o => (
                      <tr key={o.nombre}>
                        <td className={classes.td}>{o.nombre}</td>
                        <td className={`${classes.td} ${classes.tdNum}`}>
                          {o.aparecio}
                        </td>
                        <td className={`${classes.td} ${classes.tdNum}`}>
                          {o.cerro}
                        </td>
                        <td className={`${classes.td} ${classes.tdNum}`}>
                          <span className={classes.medidor}>
                            <span
                              className={classes.medidorRelleno}
                              style={{
                                width: `${o.porcentaje}%`,
                                backgroundColor: colorPorCierre(o.porcentaje),
                                display: "block",
                              }}
                            />
                          </span>
                          <span style={{ color: colorPorCierre(o.porcentaje) }}>
                            {o.porcentaje}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {datos.reforzar && (
                  <div className={classes.aviso}>
                    <Typography style={{ fontWeight: 700 }}>
                      Lo que hay que reforzar: «{datos.reforzar}»
                    </Typography>
                    <Typography
                      style={{ marginTop: 4, fontSize: "0.9rem" }}
                      color="textSecondary"
                    >
                      Es la objeción que menos termina en venta. Vale la pena
                      revisar cómo se está respondiendo y cambiar el argumento.
                    </Typography>
                  </div>
                )}
              </>
            )}
          </Paper>
        </>
      )}
    </div>
  );
};

export default Embudo;
